import React, { useEffect, useMemo, useState } from "react";
import styles from "./roofType.module.scss";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import { FileFromObjectUrl } from "../../../model/common/file-from-object-url";
import { useDispatch } from "react-redux";
import { RoofType } from "../../../model/solar-recommendation-form/roofType";
import FormWrapper from "../../../common/components/form-wrapper/FormWrapper";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import CommonFileUploadInput from "../../../common/components/file-upload-input/CommonFileUploadInput";
import { useCreateRoofTypeMutation, useDeleteRoofTypeMutation, useLazyRoofTypesQuery, useUpdateRoofTypeMutation } from "../../../service/roof-type-service";
import { hideLoader, showLoader } from "../../../stores/slices/loader-slice";
import RoofTypeItem from "./RoofTypeItem";
import CommonSelect, { CommonSelectItem } from "../../../common/components/select/CommonSelect";
import ConfirmationDialog from "../../../common/components/dialog/ConfirmationDialog";
import { BuildingType } from "../../../model/building-type";
import { getErrorMessage } from "../../../utils/error-handler";

type Props = {};

type NewCategory = {
	id: number;
	name: string;
	iconUrl: FileFromObjectUrl;
	Image: File;
	premiseType: string;
};

const RoofTypePage: React.FC<Props> = (props: Props) => {
	const buildingTypes = useMemo(
		() => [new CommonSelectItem("Bungalow" as BuildingType, "Bungalow" as BuildingType), new CommonSelectItem("Story Building" as BuildingType, "Story Building" as BuildingType)],
		[]
	);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [search, setSearch] = useState<string>("");
	const [categories, setCategories] = useState<RoofType[]>([]);
	const [filteredCategories, setFilteredCategories] = useState<RoofType[]>([]);
	const [selectedRoofType, setSelectedRoofType] = useState<RoofType | null>();
	const [isFormVisible, setIsFormVisible] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const dispatch = useDispatch();

	const [getRoofTypes, { data, isLoading, isFetching }] = useLazyRoofTypesQuery();
	const [createRoof, { isLoading: isCreating }] = useCreateRoofTypeMutation();
	const [editRoofMutattion, { isLoading: editLoading }] = useUpdateRoofTypeMutation();
	const [deleteRoof, { isLoading: isDeleting }] = useDeleteRoofTypeMutation();

	useEffect(() => {
		if (data) {
			setCategories(data);
			setFilteredCategories(data);
		}
	}, [data]);

	useEffect(() => {
		handleGetRoofTypes();
	}, []);

	useEffect(() => {
		const filtered = categories.filter(category => category.name.toLowerCase().includes(search.toLowerCase()));
		setFilteredCategories(filtered);
	}, [search, categories]);

	const {
		register,
		watch,
		formState: { errors, isValid },
		setValue,
		getValues,
		reset,
		control,
	} = useForm<NewCategory>({
		defaultValues: {
			name: undefined,
			Image: undefined,
			premiseType: undefined,
			iconUrl: {
				name: "",
				url: "",
			},
		},
		reValidateMode: "onSubmit",
		mode: "onTouched",
	});

	const handleGetRoofTypes = () => {
		dispatch(showLoader());
		getRoofTypes(null)
			.unwrap()
			.then()
			.catch(err => toast.error(err?.data?.title as string))
			.finally(() => dispatch(hideLoader()));
	};

	const addNewCategory = () => {
		const formData = new FormData();
		const obj = getValues();
		formData.append("Name", obj.name.trim());
		formData.append("PremiseType", obj.premiseType);
		formData.append("Icon", obj.Image);
		console.log("obj", obj);
		dispatch(showLoader());
		createRoof(formData)
			.unwrap()
			.then(res => {
				toast.success("roof type added successfully");
				cancelForm();
			})
			.catch(err => toast.error(getErrorMessage(err.data)))
			.finally(() => dispatch(hideLoader()));
	};

	const handleEditCategory = () => {
		const formData = new FormData();
		const obj = getValues();
		formData.append("Id", obj.id.toString());
		formData.append("Name", obj.name.trim());
		formData.append("PremiseType", obj.premiseType);
		if (obj.Image) {
			formData.append("Icon", obj.Image);
		}
		dispatch(showLoader());
		editRoofMutattion(formData)
			.unwrap()
			.then(res => {
				toast.success("Roof edited successfully");
				cancelForm();
			})
			.catch(err => toast.error(getErrorMessage(err.data)))
			.finally(() => dispatch(hideLoader()));
	};

	const handleDelete = () => {
		const formData = new FormData();
		formData.append("Id", selectedRoofType?.id?.toString() ?? "");
		deleteRoof(formData)
			.unwrap()
			.then(res => {
				toast.success("Roof deleted successfully");
				setShowDeleteConfirmation(!showDeleteConfirmation);
				setSelectedRoofType(null);
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const editRoof = async (category: RoofType) => {
		setIsEdit(true);
		setIsFormVisible(true);
		reset({
			id: category.id,
			name: category.name,
			premiseType: category.premiseType,
			// iconEdited: false,
			iconUrl: {
				url: category.iconUrl,
				name: category.iconUrl.split("/").pop()!,
			},
		});
	};

	function containsSearch() {
		if (search) {
			return styles.searchContainesText;
		}
	}

	function changeSearch(e: React.ChangeEvent<HTMLInputElement>) {
		setSearch(e.target.value.trim());
	}

	const onFilesSelection = (files: File[]) => {
		if (files.length === 0) {
			return;
		}

		const fileToSend: FileFromObjectUrl = {
			name: files[0].name,
			url: URL.createObjectURL(files[0]),
		};
		setValue("Image", files[0]);
		setValue("iconUrl", fileToSend, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
	};

	const cancelForm = () => {
		setIsFormVisible(false);
		reset();
	};

	const openNewForm = () => {
		setIsEdit(false);
		reset({
			name: "",
			Image: undefined,
			// iconEdited: false,
			iconUrl: undefined,
		});
		setIsFormVisible(true);
	};

	return (
		<>
			{showDeleteConfirmation && (
				<ConfirmationDialog
					actionLoading={isDeleting}
					btnText="Proceed"
					onCloseDialog={() => {
						setShowDeleteConfirmation(!showDeleteConfirmation);
						setSelectedRoofType(null);
					}}
					onConfirm={handleDelete}
					title="Delete Roof type"
					description=" Are you sure you want to delete roof type ?"
				/>
			)}
			<div className={styles.container}>
				<div className={styles.leftSide}>
					<FormWrapper titleSection={{ title: "Roof type", subtitle: "", buttonTitle: "Add" }} onSaveAndContinue={openNewForm}>
						<div className={styles.findApplianceContainer}>
							<div className={`${styles.searchContainer} ${containsSearch()}`}>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M15.2825 14.7176L12.5001 11.9576C13.5801 10.6109 14.1032 8.90152 13.9617 7.18103C13.8201 5.46053 13.0248 3.85964 11.7392 2.70753C10.4536 1.55543 8.77541 0.939683 7.04974 0.986903C5.32408 1.03412 3.68209 1.74072 2.46141 2.96141C1.24072 4.18209 0.534124 5.82408 0.486903 7.54974C0.439683 9.27541 1.05543 10.9536 2.20753 12.2392C3.35964 13.5248 4.96053 14.3201 6.68103 14.4617C8.40152 14.6032 10.1109 14.0801 11.4576 13.0001L14.2176 15.7601C14.2873 15.8303 14.3702 15.8861 14.4616 15.9242C14.553 15.9623 14.651 15.9819 14.7501 15.9819C14.8491 15.9819 14.9471 15.9623 15.0385 15.9242C15.1299 15.8861 15.2128 15.8303 15.2825 15.7601C15.4177 15.6202 15.4933 15.4333 15.4933 15.2388C15.4933 15.0443 15.4177 14.8574 15.2825 14.7176ZM7.25005 13.0001C6.2117 13.0001 5.19666 12.6921 4.33331 12.1153C3.46995 11.5384 2.79704 10.7185 2.39968 9.75914C2.00232 8.79983 1.89836 7.74423 2.10093 6.72583C2.3035 5.70743 2.80351 4.77197 3.53774 4.03774C4.27197 3.30351 5.20743 2.8035 6.22583 2.60093C7.24423 2.39836 8.29983 2.50232 9.25914 2.89968C10.2185 3.29704 11.0384 3.96995 11.6153 4.83331C12.1921 5.69666 12.5001 6.7117 12.5001 7.75005C12.5001 9.14244 11.9469 10.4778 10.9624 11.4624C9.97779 12.4469 8.64244 13.0001 7.25005 13.0001Z"
										fill="#2E3336"
									/>
								</svg>
								<input placeholder="Search roof types" onChange={changeSearch} />
							</div>
							<div className={styles.searchResultContainer}>
								{isFetching && <div>Updating list...</div>}
								{filteredCategories
									?.map((category, index) => (
										<RoofTypeItem
											category={category}
											key={`${index}-category${category.id}`}
											isAdded
											onDeleteAddRoofHandler={() => {
												setShowDeleteConfirmation(!showDeleteConfirmation);
												setSelectedRoofType(category);
											}}
											onEditRoofHandler={() => editRoof(category)}
										/>
									))}
							</div>
						</div>
					</FormWrapper>
				</div>
				{isFormVisible && (
					<div className={styles.rightSide}>
						<FormWrapper
							titleSection={{ title: isEdit ? "Edit roof" : "Create new Roof type", subtitle: "", buttonTitle: "Save Changes" }}
							onBack={cancelForm}
							isNotValid={!isValid || isLoading || isCreating}
							onSaveAndContinue={() => (!isEdit ? addNewCategory() : handleEditCategory())}
						>
							<div className={styles.formSection}>
								<CommonFormFieldWrapper title="Roof name" required errors={getFormInputErrors(errors.name?.message)}>
									<input {...register("name", { required: "Roof name" })} placeholder="Enter Roof Name" type="text" />
								</CommonFormFieldWrapper>

								<CommonFormFieldWrapper title="Building Type" required errors={getFormInputErrors(errors.premiseType?.message)}>
									<Controller
										name="premiseType"
										control={control}
										rules={{
											required: "Building type is required",
										}}
										render={({ field }) => (
											<CommonSelect
												items={buildingTypes}
												selected={buildingTypes.find(item => item.key === field.value) || null}
												placeholder="Select Building type"
												searchable={true}
												onChange={selectedItem => {
													const selectedValue = selectedItem ? selectedItem.key : "";
													field.onChange(selectedValue);
													setValue("premiseType", selectedValue, {
														shouldValidate: true,
														shouldDirty: true,
														shouldTouch: true,
													});
												}}
											/>
										)}
									/>
								</CommonFormFieldWrapper>

								<CommonFormFieldWrapper title="Roof Image" required errors={getFormInputErrors(errors.iconUrl?.message)}>
									<div className={styles.fileInputContainer}>
										<CommonFileUploadInput
											maxFileSize={24}
											initialFiles={[watch("iconUrl.name")]}
											accept="image/*"
											registerInputProps={register("iconUrl", { required: "Roof Image is required" })}
											onFilesSelect={onFilesSelection}
										/>
									</div>
								</CommonFormFieldWrapper>
							</div>
						</FormWrapper>
					</div>
				)}
			</div>
		</>
	);
};

export default RoofTypePage;
